export const LIGHT_UP_GAME_ID = "light_up";
export const emptyDailyPuzzleState = { puzzles: [], states: [] };
/** The puzzle for `gameId`, or with no id the first enabled puzzle of the day. */
export function todaysPuzzle(state, gameId) {
    return gameId === undefined
        ? state.puzzles.find((p) => p.enabled)
        : state.puzzles.find((p) => p.gameId === gameId);
}
export function stateFor(state, gameId) {
    return state.states.find((s) => s.gameId === gameId);
}
/** One row per puzzle on today's rota (the enabled ones: one, with the weekday rotation). */
export function dailyPuzzleRows(state) {
    return state.puzzles
        .filter((p) => p.enabled)
        .map((puzzle) => {
        const userState = stateFor(state, puzzle.gameId);
        return { puzzle, state: userState, status: dailyPuzzleStatus(userState) };
    });
}
/** Where the user is with a puzzle, from their state on it. */
export function dailyPuzzleStatus(state) {
    return state?.solved !== undefined
        ? "solved"
        : state?.startedAt !== undefined
            ? "continue"
            : "play";
}
/** The i18n key for a status: "Play", "Continue", "Solved". */
export function dailyPuzzleStatusKey(status) {
    return `dailyPuzzle.${status}`;
}
/** `state` with the user state for `next.gameId` replaced (or appended). */
export function withUserState(state, next) {
    const found = state.states.some((s) => s.gameId === next.gameId);
    return {
        ...state,
        states: found
            ? state.states.map((s) => (s.gameId === next.gameId ? next : s))
            : [...state.states, next],
    };
}
/**
 * Chit event keys as the local user index mints them: `"{number}:entry"`, `"{number}:solve"` and
 * `"{game_id}:{number}:hint:{step}:{level}"`. Entry and solve deliberately do not name the game
 * or the puzzle, so a regenerated day charges and pays once.
 */
export function parseDailyPuzzleChitKey(key) {
    const parts = key.split(":");
    if (parts.length === 2) {
        const [number, kind] = parts;
        const n = Number(number);
        if (!Number.isInteger(n) || (kind !== "entry" && kind !== "solve"))
            return undefined;
        return { number: n, kind };
    }
    if (parts.length === 5 && parts[2] === "hint") {
        const [gameId, number] = parts;
        const n = Number(number);
        if (gameId === "" || !Number.isInteger(n))
            return undefined;
        return { gameId, number: n, kind: "hint" };
    }
    return undefined;
}
/** Result cards carry the puzzle layout as hex so they render without fetching the puzzle. */
export function descriptionToHex(bytes) {
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}
/**
 * Identifies the exact puzzle a saved grid belongs to: game id, number and an FNV-1a 32-bit
 * hash of the description bytes. A regenerated puzzle with the same number gets a different
 * fingerprint, so a grid saved against the old layout is not resumed onto the new one.
 */
export function puzzleFingerprint(puzzle) {
    let h = 0x811c9dc5;
    for (const b of puzzle.description) {
        h ^= b;
        h = Math.imul(h, 0x01000193);
    }
    return `${puzzle.gameId}:${puzzle.number}:${(h >>> 0).toString(16)}`;
}
export function descriptionFromHex(hex) {
    const out = new Uint8Array(Math.floor(hex.length / 2));
    for (let i = 0; i < out.length; i++) {
        out[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
    }
    return out;
}
/** Puzzle number for a UTC timestamp in ms: the UTC day number. */
export function dailyPuzzleNumber(nowMs) {
    return Math.floor(nowMs / 86_400_000);
}
/** Grace after expiry before refetching, so the rollover push has usually landed. */
export const ROLLOVER_GRACE_MS = 5_000;
/**
 * How long until the earliest enabled puzzle expires, plus a grace, so a refetch can be timed for
 * rollover while the app is open (#9334 invariant 57). Undefined when nothing enabled is held,
 * and never negative: an already-expired puzzle asks for a refetch now.
 */
export function rolloverDelay(state, now) {
    const expiries = state.puzzles.filter((p) => p.enabled).map((p) => Number(p.expiresAt));
    if (expiries.length === 0)
        return undefined;
    return Math.max(0, Math.min(...expiries) - now) + ROLLOVER_GRACE_MS;
}
/**
 * Whether `next` is a different puzzle from `current`: another number, or the same number with
 * another layout, which is what a `regenerate_today` produces (#9334 invariant 58).
 */
export function puzzleReplaced(current, next) {
    return puzzleFingerprint(current) !== puzzleFingerprint(next);
}
